import React, { useState, useEffect } from 'react';
import { PlusIcon, XIcon, CheckIcon, AlertTriangleIcon } from 'lucide-react';
import { DEX_CONFIGS } from '../config/constantsV2';

// LocalStorage key for custom DEX configurations
const CUSTOM_DEX_STORAGE_KEY = 'chadswap_v2_custom_dexes';

const DexManager = () => {
  const [customDexes, setCustomDexes] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDex, setNewDex] = useState({
    id: '',
    name: '',
    type: 'v2',
    version: 2,
    factory: '',
    router: '',
    quoter: '', // V3 only
    positionManager: '', // V3 only
    enabled: true,
  });
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    loadCustomDexes();
  }, []);

  const loadCustomDexes = () => {
    try {
      const stored = localStorage.getItem(CUSTOM_DEX_STORAGE_KEY);
      if (stored) {
        setCustomDexes(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading custom DEXes:', error);
    }
  };

  const saveCustomDexes = (dexes) => {
    try {
      localStorage.setItem(CUSTOM_DEX_STORAGE_KEY, JSON.stringify(dexes));
      setCustomDexes(dexes);
    } catch (error) {
      console.error('Error saving custom DEXes:', error);
    }
  };

  const handleAddDex = () => {
    setFormError(null);

    // Validate required fields
    if (!newDex.id || !newDex.name || !newDex.factory || !newDex.router) {
      setFormError('Please fill in all required fields');
      return;
    }

    // Validate V3 specific fields
    if (newDex.type === 'v3' && !newDex.quoter) {
      setFormError('Quoter address is required for V3 DEXes');
      return;
    }

    // Check if ID already exists
    const allDexIds = [
      ...Object.keys(DEX_CONFIGS),
      ...customDexes.map(d => d.id),
    ];
    
    if (allDexIds.includes(newDex.id)) {
      setFormError('A DEX with this ID already exists');
      return;
    }

    // Add to custom DEXes
    const updatedDexes = [...customDexes, { 
      ...newDex,
      version: newDex.type === 'v2' ? 2 : 3,
    }];
    saveCustomDexes(updatedDexes);

    // Reset form
    setNewDex({
      id: '',
      name: '',
      type: 'v2',
      version: 2,
      factory: '',
      router: '',
      quoter: '',
      positionManager: '',
      enabled: true,
    });
    setShowAddForm(false);
  };

  const handleRemoveDex = (dexId) => {
    const updatedDexes = customDexes.filter(d => d.id !== dexId);
    saveCustomDexes(updatedDexes);
  };

  const handleToggleDex = (dexId, isBuiltIn = false) => {
    if (isBuiltIn) {
      // For built-in DEXes, we could store overrides in localStorage
      // For now, we'll just show a message
      alert('Built-in DEX settings cannot be modified from the UI. Please contact the developer.');
    } else {
      const updatedDexes = customDexes.map(d => 
        d.id === dexId ? { ...d, enabled: !d.enabled } : d
      );
      saveCustomDexes(updatedDexes);
    }
  };

  const allDexes = [
    ...Object.values(DEX_CONFIGS).map(d => ({ ...d, isBuiltIn: true })),
    ...customDexes.map(d => ({ ...d, isBuiltIn: false })),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">DEX Management</h2>
          <p className="text-white/60 text-sm mt-1">
            Configure and manage liquidity sources for V2
          </p>
        </div>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Add DEX
          </button>
        )}
      </div>

      {/* Add DEX Form */}
      {showAddForm && (
        <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Add Custom DEX</h3>
            <button
              onClick={() => {
                setShowAddForm(false);
                setFormError(null);
              }}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <XIcon className="w-5 h-5 text-white/60" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  DEX ID *
                </label>
                <input
                  type="text"
                  placeholder="e.g., spiritswap_v2"
                  value={newDex.id}
                  onChange={(e) => setNewDex({ ...newDex, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  DEX Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g., SpiritSwap V2"
                  value={newDex.name}
                  onChange={(e) => setNewDex({ ...newDex, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Type *
              </label>
              <select
                value={newDex.type}
                onChange={(e) => setNewDex({ ...newDex, type: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="v2">V2 (UniswapV2 Fork)</option>
                <option value="v3">V3 (UniswapV3 Fork)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Factory Address *
              </label>
              <input
                type="text"
                placeholder="0x..."
                value={newDex.factory}
                onChange={(e) => setNewDex({ ...newDex, factory: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Router Address *
              </label>
              <input
                type="text"
                placeholder="0x..."
                value={newDex.router}
                onChange={(e) => setNewDex({ ...newDex, router: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            {newDex.type === 'v3' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Quoter Address * (V3 only)
                  </label>
                  <input
                    type="text"
                    placeholder="0x..."
                    value={newDex.quoter}
                    onChange={(e) => setNewDex({ ...newDex, quoter: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    Position Manager (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="0x..."
                    value={newDex.positionManager}
                    onChange={(e) => setNewDex({ ...newDex, positionManager: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
              </>
            )}

            {formError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <AlertTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
                <span className="text-red-400 text-sm">{formError}</span>
              </div>
            )}

            <button
              onClick={handleAddDex}
              className="w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors"
            >
              Add DEX
            </button>
          </div>
        </div>
      )}

      {/* DEX List */}
      <div className="space-y-3">
        {allDexes.map((dex) => (
          <div
            key={dex.id}
            className="p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/[0.07] transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-white">{dex.name}</h3>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                    dex.type === 'v3' 
                      ? 'bg-purple-500/20 text-purple-300' 
                      : 'bg-blue-500/20 text-blue-300'
                  }`}>
                    {dex.type.toUpperCase()}
                  </span>
                  {dex.isBuiltIn && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-300 text-xs font-semibold rounded">
                      Built-in
                    </span>
                  )}
                  {dex.enabled ? (
                    <CheckIcon className="w-5 h-5 text-green-400" />
                  ) : (
                    <XIcon className="w-5 h-5 text-red-400" />
                  )}
                </div>
                
                <div className="space-y-1 text-sm text-white/60">
                  <div className="flex items-center gap-2">
                    <span className="w-20">Factory:</span>
                    <code className="text-xs bg-white/5 px-2 py-1 rounded">{dex.factory}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-20">Router:</span>
                    <code className="text-xs bg-white/5 px-2 py-1 rounded">{dex.router}</code>
                  </div>
                  {dex.quoter && (
                    <div className="flex items-center gap-2">
                      <span className="w-20">Quoter:</span>
                      <code className="text-xs bg-white/5 px-2 py-1 rounded">{dex.quoter}</code>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleDex(dex.id, dex.isBuiltIn)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    dex.enabled
                      ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                      : 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
                  }`}
                >
                  {dex.enabled ? 'Enabled' : 'Disabled'}
                </button>
                
                {!dex.isBuiltIn && (
                  <button
                    onClick={() => handleRemoveDex(dex.id)}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors"
                  >
                    <XIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {allDexes.length === 0 && (
        <div className="text-center py-12">
          <p className="text-white/40">No DEXes configured yet</p>
        </div>
      )}
    </div>
  );
};

export default DexManager;
